import { InfoTip, type InfoTipContent } from "@/components/InfoTip";
import { formatDate } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { NitrateNitriteStatus } from "@/types/api";

interface Props {
  status: NitrateNitriteStatus | undefined;
  loading: boolean;
  error?: boolean;
}

const PILL_CLASS: Record<NitrateNitriteStatus["state"], string> = {
  red: "pill-high",
  due: "pill-low",
  orange: "pill-medium",
  green: "pill-ok",
};

const LABEL: Record<NitrateNitriteStatus["state"], string> = {
  red: "OUT OF COMPLIANCE",
  due: "NOW DUE",
  orange: "TEST FAILED",
  green: "COMPLIANT",
};

const STATE_TIP: Record<NitrateNitriteStatus["state"], InfoTipContent> = {
  red: TIPS.nnOutOfCompliance,
  due: TIPS.nnNowDue,
  orange: TIPS.nnTestFailed,
  green: TIPS.nnCompliant,
};

export function NitrateNitriteCard({ status, loading, error = false }: Props): JSX.Element {
  // Error must not fall through to the skeleton — a failed fetch previously
  // rendered as a permanent "loading" card (pressure-test BUG-HIGH #4).
  if (error) {
    return (
      <section className="card">
        <h2 className="card-title">Nitrate / Nitrite</h2>
        <p className="text-sm text-severity-high" role="alert">
          Couldn&apos;t load nitrate/nitrite compliance. Reload the page to retry;
          if it persists, FDEP document data may be temporarily unavailable.
        </p>
      </section>
    );
  }
  if (loading || !status) {
    return (
      <section className="card">
        <h2 className="card-title">Nitrate / Nitrite</h2>
        <div className="skeleton h-12" aria-label="Loading nitrate/nitrite compliance" />
      </section>
    );
  }

  return (
    <section className="card">
      <header className="flex items-baseline justify-between mb-3">
        <h2 className="card-title mb-0">Nitrate / Nitrite</h2>
        <span className="text-xs text-ink-300 inline-flex items-center">
          Calendar year {status.year}
          <InfoTip {...TIPS.calendarYear} />
        </span>
      </header>
      <div className="flex items-center gap-2 mb-2">
        <span className={PILL_CLASS[status.state]}>{LABEL[status.state]}</span>
        <InfoTip {...STATE_TIP[status.state]} />
      </div>
      <p className="text-sm text-ink-200">{status.reason}</p>

      {status.last_test_date && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-300">Last test</dt>
          <dd>
            {formatDate(status.last_test_date)}
            {status.last_test_url && (
              <a
                href={status.last_test_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-xs text-ink-300 underline"
              >
                Open
              </a>
            )}
          </dd>
          {status.last_test_subject && (
            <>
              <dt className="text-ink-300 inline-flex items-center">Subject<InfoTip {...TIPS.docSubject} /></dt>
              <dd className="font-mono text-xs">{status.last_test_subject}</dd>
            </>
          )}
        </dl>
      )}

      {status.failure_doc_url && (
        <div className="mt-3 rounded-md bg-severity-medium/10 ring-1 ring-severity-medium/40 p-3 text-sm">
          <p className="font-medium text-severity-medium inline-flex items-center">
            Failure indicator
            <InfoTip {...TIPS.failureIndicator} />
          </p>
          {status.failure_doc_subject && (
            <p className="font-mono text-xs mt-1">{status.failure_doc_subject}</p>
          )}
          <a
            href={status.failure_doc_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block btn-primary py-1 px-2 text-xs"
          >
            Open failure doc
          </a>
        </div>
      )}
    </section>
  );
}
